import { PropsWithChildren } from "react";
import { ChallengePageStaticData } from "../_staticContent/challengePage";
import {
  LearningOpportunity,
  LearningOpportunityTopic,
} from "../server/backend-api";
import { EciuImage } from "./EciuImage";
import {
  getPaceString,
  studyFormatToString,
} from "@/server/backend-api/enumToText";
import { IconIconInfo } from "@/components/Icons";
import Link from "@/components/Link";
import { ContactUsButton } from "@/components/ContactUsButton";
import LOTableListItemMobile from "./LOTableListItemMobile";
import { IconQuestionChat } from "./Icons/IconQuestionChat";
import { useBaseContext } from "@/components/BaseContext";
import { buildCompetenceUrl, buildUrlWithQuery } from "@/utils/url";
import { StudyFormat, Tag } from "@/server/backend-api/generated";
import { BrowseQueryParams } from "../pages/browse";
import React from "react";

const texts = ChallengePageStaticData.metadata;
const fallback = "–";

type ChallengeDefinitionListItemProps = PropsWithChildren<{
  label: string;
  value?: string | null;
  labelClassName?: string;
}>;
const ListItem = (props: ChallengeDefinitionListItemProps) => (
  <div>
    <dt className={props.labelClassName}>{props.label}</dt>
    <dd className="text-style-body2 text-light-yellow">
      {props.children || props.value || fallback}
    </dd>
  </div>
);

const StudyFormatListItem = (props: ChallengeDefinitionListItemProps) => {
  const [hidden, setHidden] = React.useState(true);
  let studyFormatTooltip = "";
  switch (props.value) {
    case "Blended":
      studyFormatTooltip =
        "Learners are expected to be present on campus during some activities of the ELO while some parts are hosted online.";
      break;
    case "Hybrid":
      studyFormatTooltip =
        "Learners from partner institutions are not expected to be present on campus. No physical attendance is mandatory, nevertheless learners may be sometime allowed to join in person.";
      break;
    case "Online":
      studyFormatTooltip =
        "Learners are not expected to be present on campus at any point of the ELO.";
      break;
    case "Campus":
      studyFormatTooltip =
        "Learners are expected to be present on the actual campus of the university to attend the ELO.";
      break;
    default:
      studyFormatTooltip = "no info";
      break;
  }
  return (
    <div>
      {hidden ? null : (
        <div
          className="absolute z-50 whitespace-normal break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white focus:outline-none"
          style={{ bottom: "82%" }}
        >
          {studyFormatTooltip}
        </div>
      )}
      <dt className={props.labelClassName}>{props.label}</dt>
      <div
        className="cursor-pointer flex gap-x-xs"
        onMouseEnter={() => setHidden(false)}
        onMouseLeave={() => setHidden(true)}
      >
        <IconIconInfo className="h-md w-md flex-shrink-0" />
        <dd className="text-style-body2 text-light-yellow">
          {props.children || props.value || fallback}
        </dd>
      </div>
    </div>
  );
};

const PageListItem = (props: ChallengeDefinitionListItemProps) => (
  <ListItem {...props} labelClassName="text-style-headline5" />
);

const ApplyListItem = (props: ChallengeDefinitionListItemProps) => (
  <ListItem {...props} labelClassName="text-style-subtitle1" />
);

const VolumeOfLearningListItem = (
  props: Omit<ChallengeDefinitionListItemProps, "label"> & {
    volumeOfLearning: number;
  }
) => (
  <ListItem {...props} label={"Volume of learning"}>
    <p>{`${props.volumeOfLearning} Hours`}</p>
    <p className="text-style-caption text-white flex gap-x-xs pt-xs">
      <IconIconInfo className="h-md w-md flex-shrink-0" />
      <span>No ECTS will be awarded</span>
    </p>
  </ListItem>
);

const TopicListItem = (
  props: Omit<ChallengeDefinitionListItemProps, "label"> & {
    topics: LearningOpportunityTopic[];
  }
) => {
  if (props.topics.length === 0) {
    return <ListItem {...props} label={texts.topics} value={"-"} />;
  }
  return (
    <ListItem {...props} label={texts.topics}>
      <div className={"flex flex-col"}>
        {props.topics.map((topic) => {
          return (
            <Link
              key={topic.id}
              href={buildCompetenceUrl(topic.id, topic.name)}
              className="text-style-link1 text-red-100"
            >
              {topic.name}
            </Link>
          );
        })}
      </div>
    </ListItem>
  );
};

const TagListItem = (
  props: Omit<ChallengeDefinitionListItemProps, "label"> & {
    tags: Tag[] | null;
  }
) => {
  if (props.tags?.length === 0) {
    return <></>;
  }

  return (
    <ListItem {...props} label={texts.tags}>
      <div className={"flex flex-col"}>
        {props.tags?.map((tag) => {
          const tagUrl = buildUrlWithQuery<BrowseQueryParams>("/browse", {
            tagIds: tag.id.toString(),
          });
          return (
            <Link
              key={tag.id}
              href={tagUrl}
              className="text-style-link1 text-red-100"
            >
              {tag.name}
            </Link>
          );
        })}
      </div>
    </ListItem>
  );
};

type Props = {
  learningOpp: LearningOpportunity;
};

export const LearningOppPageMetaData = ({ learningOpp }: Props) => {
  const LO_GENERAL_QUESTION = "I have a question about a Learning Opportunity";
  const base = useBaseContext();

  return (
    <dl className="grid grid-cols-1 gap-md">
      <TopicListItem
        labelClassName={"text-style-headline5"}
        topics={learningOpp.topics}
      />
      <TagListItem
        labelClassName={"text-style-subtitle1"}
        tags={learningOpp.tags}
      />
      <StudyFormatListItem
        label={texts.studyFormat}
        value={studyFormatToString(learningOpp.studyFormat)}
      />
      <PageListItem
        label={texts.applicationPeriod}
        value={learningOpp.applicationPeriod}
      />
      <PageListItem label={texts.studyPeriod} value={learningOpp.studyPeriod} />

      {learningOpp.ects !== null && (
        <PageListItem
          label={texts.ects}
          value={`${learningOpp.ects.toString()} ECTS`}
        />
      )}

      {learningOpp.volumeOfLearning !== null &&
        learningOpp.volumeOfLearning !== 0 && (
          <VolumeOfLearningListItem
            labelClassName="text-style-headline5"
            volumeOfLearning={learningOpp.volumeOfLearning}
          />
        )}

      <PageListItem
        label={texts.hostingUniversity}
        value={learningOpp.hostingUniversity?.name ?? undefined}
      />
      {learningOpp.selfPaced && (
        <div className="font-bold text-xl">Self-paced</div>
      )}
      <div className="flex flex-row border-t-2 border-solid py-2">
        <IconQuestionChat className="w-10 h-10 p-1" />
        <div className="flex flex-col px-2">
          <span className="font-bold">Got questions?</span>
          <span>
            Reach out to us via this&nbsp;
            <ContactUsButton
              className="w-fit"
              text=" contact form"
              formProps={{
                presets: {
                  learningOppId: learningOpp.id,
                  contactFormSetting: base.contactFormSettings.find(
                    (x) => x.name === LO_GENERAL_QUESTION
                  ),
                },
              }}
              memberUniversities={base.memberUniversities}
              contactFormSettings={base.contactFormSettings}
              learningOppNames={base.learningOppNames}
            />
          </span>
        </div>
      </div>
    </dl>
  );
};

export const LearningOppApplyMetaData = ({ learningOpp }: Props) => (
  <div className="bg-blue text-white rounded-3 p-lg space-y-lg">
    <div className="flex gap-md items-center">
      <div className="w-[48px] h-[48px]">
        <EciuImage rounded={"3"} src={learningOpp.imageUri} />
      </div>
      <a
        className="text-style-headline5 hover:underline"
        href={learningOpp.url}
      >
        {learningOpp.title}
      </a>
    </div>
    <dl className="grid grid-cols-1 tablet-lg:grid-cols-2 gap-y-md gap-x-3xl">
      {/*TODO: Remove topic text that should not be here.*/}
      <TopicListItem
        labelClassName={"text-style-subtitle1"}
        topics={learningOpp.topics}
      />
      <ApplyListItem
        label={texts.studyFormat}
        value={studyFormatToString(learningOpp.studyFormat)}
      />
      <ApplyListItem
        label={texts.applicationPeriod}
        value={learningOpp.applicationPeriod}
      />
      <ApplyListItem
        label={texts.studyPeriod}
        value={learningOpp.studyPeriod}
      />
      {learningOpp.ects !== null && (
        <ApplyListItem
          label={texts.ects}
          value={`${learningOpp.ects.toString()} ECTS`}
        />
      )}
      <ApplyListItem label={texts.ects} value={`${learningOpp.ects} ECTS`} />
      {learningOpp.volumeOfLearning !== null && (
        <VolumeOfLearningListItem
          labelClassName={"text-style-subtitle1"}
          volumeOfLearning={learningOpp.volumeOfLearning}
        />
      )}
      <ApplyListItem
        label={texts.hostingUniversity}
        value={learningOpp.hostingUniversityName ?? undefined}
      />
      <TagListItem
        labelClassName={"text-style-subtitle1"}
        tags={learningOpp.tags}
      />
    </dl>
  </div>
);
